import { useEffect, useState, useRef, useContext } from 'react';
import OptionCard from 'src/components/OptionCard';
import { ApiRoutes } from 'src/lib/routes';
import { Option, Pagination, FilterableType } from 'src/types';
import useQuery from 'src/hooks/useQuery';
import classNames from 'classnames';
import { generateArrayDates, getEleOffset } from 'src/helpers';
import useSearchBarState from 'src/hooks/useSearchBarState';
import Router from 'next/router';
import { OtherOptionCacheContext } from 'src/contexts/OtherOptionCacheProvider';
import { omit } from 'lodash-es';
import Loading from './Loading';

type Props = {
  hideTitle?: boolean;
  renderTitle?: () => JSX.Element;
  isGiftExperience?: boolean;
  hideLoadMore?: boolean;
  scrollLoadMore?: boolean;
  noQuery?: boolean;
  fallbackData?: Option[];
  afterFetch?: (count: number) => void;
  record: {
    id?: string | number;
    type: FilterableType;
  };
  queryParams?: {
    [key: string]: string | number | string[] | number[] | null | (number | string | undefined)[];
  };
  hidePrice?: boolean;
  shouldScrollTop?: boolean;
  useHtmlLink?: boolean;
  show?: boolean;
};

type ListOption = { data: Option[]; pagination: Pagination };

const OtherOptions: React.FC<Props> = ({
  hideTitle,
  renderTitle,
  isGiftExperience,
  hideLoadMore,
  scrollLoadMore,
  noQuery,
  fallbackData,
  record,
  queryParams,
  afterFetch,
  hidePrice,
  shouldScrollTop,
  useHtmlLink,
  show = true
}) => {
  const { cache, setCache } = useContext(OtherOptionCacheContext);
  const [searchParams] = useSearchBarState();
  const scrollLoadMoreRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const fallback: ListOption = { data: fallbackData || [], pagination: {} as Pagination };
  const reqParams = {
    per_page: 12,
    record_id: record.id,
    record_type: record.type,
    page: page,
    start_date:
      searchParams.dates.length === 2
        ? generateArrayDates(searchParams.dates[0], searchParams.dates[1])
        : searchParams.dates,
    qty: searchParams.numGuest,
    ...(show ? queryParams || { sort_by: 'recommended' } : {}),
  };
  const { data, isLoading } = useQuery<ListOption>(
    noQuery
      ? null
      : (record.id && record.type || record.type === 'Fts')
      ? ApiRoutes.apiFilterRoute({}, reqParams).toUrl()
      : null
  );
  const [options, setOptions] = useState<Option[]>([]);

  const { data: fetchedOptions, pagination } = data || fallback;

  useEffect(() => {
    if (page > 1) {
      setOptions((prev) => [...prev, ...fetchedOptions]);
    } else {
      setOptions(fetchedOptions);
      const timer = setTimeout(() => {
        if (shouldScrollTop) {
          window.scrollTo(0, 0);
        }
      }, 200);

      return () => {
        window.clearTimeout(timer);
      };
    }
  }, [fetchedOptions.map((o) => o.id).join(','), page]);

  useEffect(() => {
    if (typeof afterFetch === 'function') {
      afterFetch(pagination.totalCount);
    }
  }, [pagination.totalCount]);

  useEffect(() => {
    if (!scrollLoadMore) return;
    if (!scrollLoadMoreRef.current) return;
    if (!pagination.nextPage) return;
    const nextPage = pagination.nextPage;
    const scrollLoadMoreEle = scrollLoadMoreRef.current;
    const loadMore = () => {
      if (isLoading) return;
      const currentPos = window.pageYOffset;
      const loadMorePos = getEleOffset(scrollLoadMoreEle).top - window.innerHeight;
      if (currentPos > loadMorePos) {
        setPage(nextPage);
      }
    };

    document.addEventListener('scroll', loadMore);

    return () => {
      document.removeEventListener('scroll', loadMore);
    };
  }, [scrollLoadMore, pagination.nextPage, isLoading]);

  const cacheKey = `${record.type}_${record.id}`;
  const cachePage = cache.page ? cache.page[cacheKey] : undefined;
  const numPage = Math.ceil(options.length / reqParams.per_page);
  const cacheScrollPosition = cache.scrollPosition ? cache.scrollPosition[cacheKey] : undefined;
  const shouldScrollRestore = cache.shouldScrollRestore;

  useEffect(() => {
    setPage(1);
  }, [record.id, record.type, JSON.stringify(searchParams), JSON.stringify(queryParams)]);

  useEffect(() => {
    const onRouteChangeStart = () => {
      setCache((prev) => ({
        ...prev,
        scrollPosition: {
          ...(prev.scrollPosition || {}),
          [cacheKey]: { x: window.scrollX, y: window.scrollY },
        },
        page: { ...(prev.page || {}), [cacheKey]: page },
      }));
    };

    const timer = setTimeout(() => {
      if (typeof cachePage === 'number' && cachePage > 1) {
        if (page < cachePage) {
          setPage((prev) => prev + 1);
        }
      }
    }, 1);

    Router.events.on('routeChangeStart', onRouteChangeStart);

    return () => {
      Router.events.off('routeChangeStart', onRouteChangeStart);
      clearTimeout(timer);
    };
  }, [record.id, record.type, page, cachePage]);

  useEffect(() => {
    if (
      shouldScrollRestore &&
      cachePage &&
      numPage &&
      numPage === cachePage &&
      typeof cacheScrollPosition?.x === 'number' &&
      typeof cacheScrollPosition?.y === 'number'
    ) {
      window.scrollTo(cacheScrollPosition.x, cacheScrollPosition.y);
      setCache((prev) => ({
        ...prev,
        shouldScrollRestore: false,
      }));
    }
  }, [
    cacheKey,
    numPage,
    cachePage,
    cacheScrollPosition?.x,
    cacheScrollPosition?.y,
    shouldScrollRestore,
  ]);

  return show ? (
    <>
      {!hideTitle && (
        <div className="container">
          {renderTitle ? (
            renderTitle()
          ) : (
            <h2 className="tw-font-semibold tw-text-xl md:tw-text-2xl mb-4">Explore All</h2>
          )}
        </div>
      )}

      <div className="container">
        <div className="row">
          {options.map((option) => (
            <OptionCard
              key={option.id}
              option={option}
              highlightText={record.type === 'Fts' ? String(record.id) : undefined}
              isGift={isGiftExperience}
              hidePrice={hidePrice}
              useHtmlLink={useHtmlLink}
            />
          ))}
        </div>
      </div>

      <div ref={scrollLoadMoreRef} />

      {isLoading ? (
        <div className="container tw-py-16">
          <Loading className="tw-justify-center" />
        </div>
      ): null}

      {!hideLoadMore && !scrollLoadMore && pagination.nextPage && (
        <div className="tw-mt-0 md:tw-mt-12 tw-text-center">
          <button
            type="button"
            className="btn btn-primary"
            disabled={isLoading}
            onClick={() => {
              setPage((prev) => prev + 1);
            }}
          >
            Load More
          </button>
        </div>
      )}
    </>
  ) : null;
};

export default OtherOptions;
